import { ActionIcon, Badge, Box, Center, Flex, Loader, Modal, Stack, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { MedplumClient } from '@medplum/core';
import { Attachment } from '@medplum/fhirtypes';
import { IconDownload, IconExternalLink, IconX } from '@tabler/icons-react';
import { useState } from 'react';
import { downloadAttachment } from '@/services/attachments';
import classes from './TimelineAttachments.module.css';
import commonStyles from '@/theme/common.module.css';
import { AttachmentFileIcon } from '@/components/icons/AttachmentFileIcon';

interface TextMessageTimelineImagesProps {
  mediaAttachments: Attachment[];
  pdfAttachments: Attachment[];
  medplum: MedplumClient;
}

export function TimelineAttachments({ medplum, mediaAttachments, pdfAttachments }: TextMessageTimelineImagesProps) {
  const [opened, { open, close }] = useDisclosure(false);
  const [selectedImage, setSelectedImage] = useState<{ url: string; title: string } | null>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [downloadingIndex, setDownloadingIndex] = useState<Set<number>>(new Set());
  const [isZoomed, setIsZoomed] = useState(false);

  const handleImageClick = (attachment: Attachment) => {
    setSelectedImage({
      url: attachment.url || '',
      title: attachment.title || 'Image',
    });
    setIsZoomed(false);
    open();
  };

  const handleModalImageClick = () => {
    setIsZoomed(!isZoomed);
  };

  async function handleDownload(attachment: Attachment, event: React.MouseEvent, index: number) {
    event.stopPropagation();
    setDownloadingIndex((prev) => new Set(prev).add(index));

    try {
      await downloadAttachment(medplum, attachment);
    } catch {
      // Error handling is done in the service function
    } finally {
      setDownloadingIndex((prev) => {
        const newSet = new Set(prev);
        newSet.delete(index);
        return newSet;
      });
    }
  }

  return (
    <>
      <Flex gap="xs" mt="md" wrap="wrap" justify="space-between" align="flex-end">
        {mediaAttachments.map((attachment, index) => (
          <Box
            key={index}
            className={classes.imageWrapper}
            onClick={() => {
              if (downloadingIndex.has(index)) return;
              handleImageClick(attachment);
            }}
            onMouseEnter={() => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex(null)}
          >
            <img src={attachment.url} alt={attachment.title || `Attachment ${index + 1}`} className={classes.image} />

            {hoveredIndex === index && !downloadingIndex.has(index) && (
              <Box className={classes.overlay}>
                <Center className={classes.overlayContent}>
                  <ActionIcon
                    onClick={(e) => handleDownload(attachment, e, index)}
                    variant="filled"
                    color="blue"
                    size="lg"
                    radius="md"
                    className={classes.actionIcon}
                  >
                    <IconDownload size={18} />
                  </ActionIcon>
                </Center>
              </Box>
            )}

            {downloadingIndex.has(index) && (
              <Box className={classes.loading}>
                <Loader size="md" color="blue" />
              </Box>
            )}
          </Box>
        ))}

        {pdfAttachments?.length > 0 && (
          <Stack gap="xs" mt="xs">
            {pdfAttachments.map((attachment, index) => (
              <Badge
                key={index}
                variant="light"
                color="#246A7A"
                size="lg"
                style={{
                  width: 'fit-content',
                  maxWidth: '100%',
                  position: 'relative',
                  cursor: 'pointer',
                }}
                leftSection={<AttachmentFileIcon attachment={attachment} />}
                rightSection={<IconExternalLink size={16} />}
                onClick={() => {
                  window.open(attachment.url, '_blank');
                }}
                className={commonStyles.badge}
              >
                <Text size="xs" truncate style={{ maxWidth: '180px' }}>
                  {attachment.title || `document-${index + 1}.pdf`}
                </Text>
              </Badge>
            ))}
          </Stack>
        )}
      </Flex>

      <Modal
        opened={opened}
        onClose={() => {
          close();
          setIsZoomed(false);
        }}
        size="auto"
        centered
        withCloseButton={false}
        padding={0}
        classNames={{
          content: classes.modalContent,
          inner: classes.modalInner,
        }}
        styles={{
          content: { overflow: 'visible', position: 'relative' },
        }}
      >
        {selectedImage && (
          <>
            <ActionIcon
              variant="filled"
              size="lg"
              radius="xl"
              onClick={() => {
                close();
                setIsZoomed(false);
              }}
              className={classes.modalCloseButton}
            >
              <IconX size={20} />
            </ActionIcon>

            <div className={`${classes.imageContainer} ${isZoomed ? classes.imageContainerZoomed : ''}`}>
              <img
                src={selectedImage.url}
                alt={selectedImage.title}
                className={`${classes.modalImage} ${isZoomed ? classes.modalImageZoomed : ''}`}
                onClick={handleModalImageClick}
              />
            </div>
          </>
        )}
      </Modal>
    </>
  );
}
